import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Salon } from '../../../Configuracion/types';
import { toast } from 'react-toastify';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Pin, PersonWorkspace } from 'react-bootstrap-icons';
import { FaSearchPlus, FaSearchMinus } from 'react-icons/fa';
import { FaArrowsRotate, FaObjectGroup, FaGear } from 'react-icons/fa6';
import {mergeMesasByList , unMergeMesasByList} from '../../../../../crud/management_api/mesas';
import './ActionSection.scss';
import { FaBan, FaCheck } from 'react-icons/fa6';
import ModalHelper from './components/ModalHelper/ModalHelper';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { clearEntireCache } from '../../../../../crud/cache/cachePublicMethods';
import { localDB } from '../../../../../crud/localDB/localDB';
import Configuration from './components/Configuration/Configuration';
import { DisplayPrefs } from '../types';

interface ActionSectionProps {
  selectedSalon?: Salon;
  hosterMode: boolean;
  setHosterMode: (mode: boolean) => void;
  salones: Salon[];
  onSelectSalon: (salon: Salon) => void;
  scaleFactor: number;
  setScaleFactor: any;
  mergeMode: boolean;
  setMergeMode: (mode: boolean) => void;
  mergedMesas?: any[];
  setMergedMesas?: (mesas: any[]) => void;
  // New: desktop-only option to view all salones as a grid
  showAllSalones?: boolean;
  setShowAllSalones?: (show: boolean) => void;
  onSelectAllSalones?: () => void;
  // New: presentation toggle (mapa/lista/pedidos)
  viewPresentation?: 'mapa' | 'lista' | 'pedidos';
  setViewPresentation?: (v: 'mapa' | 'lista' | 'pedidos') => void;
  // Callback when salon is pinned
  onSalonFixed?: (fixedSalonData: { salon_id?: number; salon_name?: string; isVerTodos?: boolean }) => void;
  // Display preferences & updater
  displayPrefs?: DisplayPrefs;
  onUpdateDisplayPrefs?: (next: DisplayPrefs) => void;
}

const ActionSection: React.FC<ActionSectionProps> = ({
  selectedSalon,
  hosterMode,
  setHosterMode,
  salones,
  onSelectSalon,
  scaleFactor,
  setScaleFactor,
  mergeMode,
  setMergeMode,
  mergedMesas = [],
  setMergedMesas = () => {},
  showAllSalones = false,
  setShowAllSalones = () => {},
  onSelectAllSalones = () => {},
  viewPresentation = 'mapa',
  setViewPresentation = () => {},
  onSalonFixed = () => {},
  displayPrefs = { showNumber: true, showTag: false, showElapsed: true, showCharges: true, showWaiter: true, showPeopleType: 'capacity' },
  onUpdateDisplayPrefs = () => {}
}) => {
const [showModal, setShowModal] = useState(false);
const [showConfig, setShowConfig] = useState(false);
const [fixedSalon, setFixedSalon] = useState<{ salon_id?: number; salon_name?: string; isVerTodos?: boolean } | null>(null);

// Load persisted preferences (pinned salon, zoom, view)
useEffect(() => {
  let mounted = true;
  (async () => {
    try {
      const pinned = await localDB.get<{ salon_id?: number; salon_name?: string; isVerTodos?: boolean }>('restaurant', 'pinnedSalon');
      const zoom = await localDB.get<number>('restaurant', 'salonZoom');
      const view = await localDB.get<'mapa' | 'lista' | 'pedidos'>('restaurant', 'viewPresentation');
      if (!mounted) return;
      if (pinned) {
        setFixedSalon(pinned);
        // If "Ver Todos" is pinned, activate it on load
        if (pinned.isVerTodos) {
          onSelectAllSalones();
        }
      }
      // Only apply saved zoom on desktop (> 900px). Mobile always uses fixed scale.
      if (typeof zoom === 'number' && !Number.isNaN(zoom) && window.innerWidth > 900) setScaleFactor(zoom);
      if (view === 'mapa' || view === 'lista' || view === 'pedidos') setViewPresentation(view);
    } catch {
      // no-op
    }
  })();
  return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // If switching to list or pedidos view, ensure merge mode is off
  useEffect(() => {
    if ((viewPresentation === 'lista' || viewPresentation === 'pedidos') && mergeMode) {
      setMergeMode(false);
      setMergedMesas([]);
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [viewPresentation]);
const handleFijarSalon = useCallback(() => {
  // Check if "Ver Todos" is selected
  if (showAllSalones) {
    const pinnedVerTodos = { isVerTodos: true };
    void localDB.set('restaurant', 'pinnedSalon', pinnedVerTodos);
    setFixedSalon(pinnedVerTodos);
    toast.success('"Ver Todos" fijado exitosamente');
    return;
  }
  
  // Handle regular salon pinning
  if (!selectedSalon) return toast.info('Primero selecciona un salón');
  const idRaw = (selectedSalon as any).salon_id;
  const idNum = typeof idRaw === 'string' ? Number(idRaw) : idRaw;
  const persistedPinned = { salon_id: Number.isFinite(idNum) ? (idNum as number) : null, salon_name: selectedSalon.salon_name };
  const statePinned = { salon_id: Number.isFinite(idNum) ? (idNum as number) : undefined, salon_name: selectedSalon.salon_name };
  
  void localDB.set('restaurant', 'pinnedSalon', persistedPinned);
  setFixedSalon(statePinned);
  
  // Notify parent component about the pinned salon
  onSalonFixed(statePinned);
  
  toast.success('Salón fijado exitosamente');
}, [showAllSalones, selectedSalon, onSalonFixed]);

const handleDesfijarSalon = useCallback(() => {
  void localDB.set('restaurant', 'pinnedSalon', null);
  setFixedSalon(null);
  
  // Don't auto-select another salon, just remove the pin
  toast.success('Salón desfijado exitosamente');
}, []);


  const renderZoomButtons = useCallback(() => (
    <ButtonGroup className="action-btn__zoom">
      <Button
        variant="none"
        className="mp-button light"
        onClick={() => setScaleFactor((f:any) => { 
          const nv = Math.max(0.1, f - 0.1); 
          // Only save zoom on desktop, not on mobile
          if (window.innerWidth > 900) void localDB.set('restaurant','salonZoom', nv); 
          return nv; 
        })}
        aria-label="Disminuir zoom"
      >
        <FaSearchMinus />
      </Button>
      <Button
        variant="none"
        className="mp-button light"
        onClick={() => setScaleFactor((f:any) => { 
          const nv = f + 0.1; 
          // Only save zoom on desktop, not on mobile
          if (window.innerWidth > 900) void localDB.set('restaurant','salonZoom', nv); 
          return nv; 
        })}
        aria-label="Aumentar zoom"
      >
        <FaSearchPlus />
      </Button>
    </ButtonGroup>
  ), [setScaleFactor]);

const renderSalonSelector = useMemo(() => (
  <div className="salon-selector">
    {/* Synthetic option: Ver Todos (available on all viewports). On small screens, force Lista view. */}
    {typeof window !== 'undefined' && (
      <div
        key="all"
        className={`salon-selector__item${showAllSalones ? ' salon-selector__item--selected' : ''}`}
        onClick={() => {
          onSelectAllSalones();
          if (window.innerWidth <= 900) {
            setViewPresentation('lista');
            void localDB.set('restaurant', 'viewPresentation', 'lista');
          }
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLDivElement).click(); } }}
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        aria-label="Ver todos los salones"
      >
        Ver Todos
        {fixedSalon?.isVerTodos && <Pin aria-label="Fijado"/>}
      </div>
    )}
    {salones.map((salon, i) => {
      const isSelected = selectedSalon?.salon_name === salon.salon_name;
      const isFixed =
        !!fixedSalon &&
        !fixedSalon.isVerTodos &&
        ((fixedSalon.salon_id != null && String(salon.salon_id) === String(fixedSalon.salon_id)) ||
         (fixedSalon.salon_name && salon.salon_name === fixedSalon.salon_name));

      return (
        <div
          key={i}
          className={`salon-selector__item${isSelected && !showAllSalones ? ' salon-selector__item--selected' : ''}`}
          onClick={() => onSelectSalon(salon)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLDivElement).click(); } }}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          aria-label={`Seleccionar salón ${salon.salon_name}`}
        >
          {salon.salon_name}
          {isFixed && <Pin aria-label="Fijado"/>} 
        </div>
      );
    })}
  </div>
), [salones, selectedSalon, showAllSalones, fixedSalon, onSelectSalon, onSelectAllSalones, setViewPresentation]);


  const handleMergeMesas = useCallback(async () => {
    const mesaIds = mergedMesas.map(mesa => mesa.id);
    try {
      await mergeMesasByList(mesaIds);
      toast.success('Mesas unidas exitosamente');
      setMergedMesas([]);
      setMergeMode(false);
      window.location.reload();
    } catch (error) {
      console.error('Error al unir mesas:', error);
      toast.error('Error al unir mesas');
    }
  }, [mergedMesas, setMergedMesas, setMergeMode]);

  // Desvincular mesas seleccionadas
  const handleCancelMerge = useCallback(async () => {
    if (mergedMesas.length > 0) {
      const mesaIds = mergedMesas.map(mesa => mesa.id);
      try {
        await unMergeMesasByList(mesaIds);
        toast.success('Mesas desvinculadas exitosamente');
        setMergedMesas([]);
        setMergeMode(false);
        window.location.reload();
      } catch (error) {
        console.error('Error al desvincular mesas:', error);
        toast.error('Error al desvincular mesas');
      }
    } else {
      setMergedMesas([]);
      setMergeMode(false);
    }
  }, [mergedMesas, setMergedMesas, setMergeMode]);

  const handleRefresh = useCallback(async () => {
    await clearEntireCache();
    window.location.reload();
  }, []);

  return (
    <div className="action-section-card">
      <div className="action-section-card__row action-section-card__row--actions">
        <div className="action-btns">
          
          <div className='par-action-btns'>
            {/* Configuration (gear) */}
    {!mergeMode && (
              <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-config">Configuración de vista</Tooltip>}>
                <Button 
                  variant="none" 
                  className="mp-button light" 
                  onClick={() => setShowConfig(true)}
                  aria-label="Configuración de vista"
                >
      <FaGear /> <span>Configurar</span>
                </Button>
              </OverlayTrigger>
            )}
            {!mergeMode && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-fijar-salon">
                    {fixedSalon ? 'Desfijar salón' : 'Fijar salón'}
                  </Tooltip>
                }
              >
                <Button 
                  variant="none" 
                  className={`mp-button light ${fixedSalon ? 'is-fixed' : ''}`}
                  onClick={fixedSalon ? handleDesfijarSalon : handleFijarSalon}
                  aria-label={fixedSalon ? 'Desfijar salón' : 'Fijar salón'}
                  aria-pressed={!!fixedSalon}
                >
                  <Pin /> <span>{fixedSalon ? 'Desfijar Salón' : 'Fijar Salón'}</span>
                </Button>
              </OverlayTrigger>
            )}
            {viewPresentation === 'mapa' && !mergeMode && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-juntar-mesas">Juntar mesas</Tooltip>}
              >
                <Button
                  variant='none'
                  className="mp-button light"
                  onClick={() => setMergeMode(!mergeMode)}
                  aria-label="Juntar mesas"
                  aria-pressed={mergeMode}
                >
                  <FaObjectGroup/> <span>Juntar mesas</span>
                </Button>
              </OverlayTrigger>
            )}
            {mergeMode && viewPresentation === 'mapa' && mergedMesas.length <= 1 && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-desvincular">Desvincular mesas</Tooltip>}
              >
                <Button
                  variant="none"
                  className="mp-button light"
                  onClick={handleCancelMerge}
                  aria-label="Desvincular mesas"
                >
                  <FaBan /> <span>Desvincular</span>
                </Button>
              </OverlayTrigger>
            )}
          </div>

          {mergeMode && viewPresentation === 'mapa' && mergedMesas.length > 1 && (
            <div className="merge-action-btns">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-confirmar-union">Confirmar unión de mesas</Tooltip>}
              >
                <Button
                  variant="none"
                  className="mp-button confirm"
                  onClick={handleMergeMesas}
                  aria-label="Confirmar unión de mesas"
                >
                  <FaCheck /> <span>Confirmar</span>
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-cancelar-union">Cancelar unión de mesas</Tooltip>}
              >
                <Button
                  variant="none"
                  className="mp-button cancel"
                  onClick={handleCancelMerge}
                  aria-label="Cancelar unión de mesas"
                >
                  <FaBan /> <span>Cancelar</span>
                </Button>
              </OverlayTrigger>
            </div>
          )}

          <div className='par-action-btns'>
            {!mergeMode &&  (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-refrescar">Refrescar</Tooltip>}
              >
                <Button
                  variant="none"
                  className="mp-button light"
                  onClick={handleRefresh}
                  aria-label="Refrescar"
                >
                  <FaArrowsRotate /> <span>Refrescar</span>
                </Button>
              </OverlayTrigger>
            )}

            {!mergeMode && viewPresentation === 'mapa' && renderZoomButtons()}
          </div>
          
        </div>
        {/* Thin toggle: Mapa | Lista | Pedidos */}
        <div className="view-toggle-thin">
          <button
            className={`vt-option ${viewPresentation === 'mapa' ? 'active' : ''}`}
            onClick={() => { setViewPresentation('mapa'); void localDB.set('restaurant', 'viewPresentation', 'mapa'); }}
            aria-pressed={viewPresentation === 'mapa'}
          >
            Mapa
          </button>
          <span className="vt-sep">|</span>
          <button
            className={`vt-option ${viewPresentation === 'lista' ? 'active' : ''}`}
            onClick={() => { setViewPresentation('lista'); void localDB.set('restaurant', 'viewPresentation', 'lista'); }}
            aria-pressed={viewPresentation === 'lista'}
          >
            Lista
          </button>
          <span className="vt-sep">|</span>
          <button
            className={`vt-option ${viewPresentation === 'pedidos' ? 'active' : ''}`}
            onClick={() => { 
              setViewPresentation('pedidos'); 
              void localDB.set('restaurant', 'viewPresentation', 'pedidos');
              // Always reset showAllSalones when switching to Pedidos view
              setShowAllSalones(false);
            }}
            aria-pressed={viewPresentation === 'pedidos'}
          >
            Pedidos
          </button>
        </div>
        {!mergeMode &&
        <div className='container-tooltip-switch'>
          <OverlayTrigger
            placement="left"
            overlay={
              <Tooltip id="tooltip-hostermode">
                Modo hoster/anfitrión: el anfitrión puede visualizar las reservas del día, crear una reservación, lista de espera (disponibilidad de mesas en tiempo real) y ver la cantidad de mesas que están atendiendo los meseros. 
              </Tooltip>
            }
          >
            <span className="tooltip-icon" onClick={() => setShowModal(true)}>
              <FaInfoCircle />
            </span>
          </OverlayTrigger>
           <label className="hoster-mode-toggle">
              <input
                type="checkbox"
                checked={hosterMode}
                onChange={() => setHosterMode(!hosterMode)}
              />
              <span className="slider">
                <span className="toggle-icon">
                  <PersonWorkspace className={hosterMode ? 'active' : undefined} />
                </span>
              </span>
            </label>
        </div>}
      </div>
      {/* Only show salon selector in Mapa and Lista views, not in Pedidos */}
      {viewPresentation !== 'pedidos' && (
        <div className="action-section-card__row action-section-card__row--selector">
          {renderSalonSelector}
        </div>
      )}
      {showModal && (
        <ModalHelper show={showModal} onClose={() => setShowModal(false)} />
      )}
      {showConfig && (
        <Configuration
          show={showConfig}
          handleClose={() => setShowConfig(false)}
          prefs={displayPrefs}
          onSave={(next) => onUpdateDisplayPrefs && onUpdateDisplayPrefs(next)}
        />
      )}
    </div>
  );
};

export default ActionSection;
